'use client';

import {
  isVideoThumbnailUrl,
  resolvePopClipThumbnailUrl,
} from '@ps/functions/clips/popclip-thumbnail';
import type { PopClipMetadata } from '@ps/models/lib/popclip';
import type { realestatelst } from '@ps/models/lib/realestate-listing-page';
import { VideoPoster } from '@ps/ui/atoms/video-poster';
import { MeasuredContainer } from '@ps/ui/molecules/measured-container';

import {
  HEIGHT_BOOST,
  REFERENCE_WIDTH,
  computeSeriesContentMetrics,
} from './functions/series-content-metrics';

// ─── SeriesViewSkeleton ──────────────────────────────────────────────

interface SeriesViewSkeletonProps {
  snapshot?: realestatelst.VideoSeriesItemSnapshot;
  /** Container width for accurate metrics. When omitted, self-measures. */
  width?: number;
}

/**
 * Single skeleton for SeriesView — used everywhere:
 * - The video-series widget's `<Suspense>` fallback while its server rack
 *   streams (has snapshot)
 * - `MeasuredContainer` fallback before width measurement (has snapshot)
 * - Editor widget skeleton (no snapshot)
 *
 * When `width` is provided, renders with accurate computed metrics.
 * When omitted, wraps itself in `MeasuredContainer` to self-measure.
 */
export function SeriesViewSkeleton({
  snapshot,
  width,
}: SeriesViewSkeletonProps) {
  if (width != null) {
    return <SeriesViewSkeletonBody snapshot={snapshot} width={width} />;
  }

  return (
    <MeasuredContainer
      aspectRatio="5/3"
      fallback={<SeriesViewSkeletonBody snapshot={snapshot} />}
    >
      {measuredWidth => (
        <SeriesViewSkeletonBody snapshot={snapshot} width={measuredWidth} />
      )}
    </MeasuredContainer>
  );
}

// ─── Body (renders with or without measured width) ───────────────────

function SeriesViewSkeletonBody({
  snapshot,
  width,
}: {
  snapshot?: realestatelst.VideoSeriesItemSnapshot;
  width?: number;
}) {
  const bannerUrl =
    snapshot?.mobileBannerImage ?? snapshot?.desktopBannerImage ?? null;
  const elements = snapshot?.elements ?? [];

  const metrics = width != null ? computeSeriesContentMetrics(width) : null;
  const scale = width != null ? width / REFERENCE_WIDTH : null;
  const heightScale = scale != null ? scale * HEIGHT_BOOST : null;
  const footerHeight =
    heightScale != null ? Math.round(50 * heightScale) : undefined;
  const footerPx = scale != null ? Math.round(19 * scale) : undefined;
  const scrollPx = scale != null ? Math.round(16 * scale) : undefined;
  const sectionGap =
    heightScale != null ? Math.round(8 * heightScale) : undefined;

  return (
    <div
      className="flex flex-col overflow-hidden rounded-lg border"
      style={{
        borderColor: 'hsl(var(--content-1) / 0.17)',
        backgroundColor: 'hsl(var(--content-1))',
      }}
    >
      {/* Banner */}
      {bannerUrl ? (
        <div
          className="shrink-0 overflow-hidden"
          style={{
            maskImage:
              'linear-gradient(to bottom, rgba(0,0,0,1) calc(100% - 36px), rgba(0,0,0,0))',
            WebkitMaskImage:
              'linear-gradient(to bottom, rgba(0,0,0,1) calc(100% - 36px), rgba(0,0,0,0))',
          }}
        >
          <img
            src={bannerUrl}
            alt="Series banner"
            className="w-full rounded-lg object-cover"
            style={{ aspectRatio: 5 / HEIGHT_BOOST }}
          />
        </div>
      ) : (
        <div
          className="animate-pulse"
          style={{
            aspectRatio: `${5 / HEIGHT_BOOST}`,
            backgroundColor: 'hsl(var(--info))',
          }}
        />
      )}

      {/* Card row */}
      {metrics ? (
        <div style={{ marginTop: sectionGap }}>
          <div
            className="no-scrollbar flex overflow-x-auto"
            style={{
              gap: metrics.gap,
              paddingLeft: scrollPx,
              paddingRight: scrollPx,
            }}
          >
            {elements.length > 0
              ? elements.map(element => {
                  const metaData = element.popClip?.metaData as
                    | PopClipMetadata
                    | undefined;
                  const thumbnailUrl = resolvePopClipThumbnailUrl(
                    metaData,
                    element.popClip?.cdnUrl,
                  );
                  const title = metaData?.videoTitle ?? '';

                  return (
                    <div
                      key={element.id}
                      className="relative shrink-0 overflow-hidden rounded-lg"
                      style={{
                        width: metrics.itemWidth,
                        height: metrics.itemHeight,
                      }}
                    >
                      {thumbnailUrl ? (
                        isVideoThumbnailUrl(thumbnailUrl) ? (
                          // Video-cover thumbnail (`.mp4`) → first frame in a
                          // <video>, not an <img>.
                          <VideoPoster
                            src={thumbnailUrl}
                            aria-label={title}
                            className="h-full w-full"
                          />
                        ) : (
                          <img
                            src={thumbnailUrl}
                            alt={title}
                            loading="lazy"
                            className="h-full w-full object-cover"
                          />
                        )
                      ) : (
                        <div
                          className="h-full w-full"
                          style={{ backgroundColor: 'hsl(var(--info))' }}
                        />
                      )}
                      <div
                        className="pointer-events-none absolute inset-x-0 bottom-0"
                        style={{
                          height: Math.max(
                            16,
                            Math.round(36 * (heightScale ?? 1)),
                          ),
                          background:
                            'linear-gradient(to top, rgba(0,0,0,0.4), rgba(0,0,0,0))',
                        }}
                      />
                      {title && (
                        <p
                          className="text-content-1 absolute inset-x-1 bottom-1 truncate drop-shadow"
                          style={{
                            fontSize: Math.max(
                              8,
                              Math.round(12 * (heightScale ?? 1)),
                            ),
                          }}
                        >
                          {title}
                        </p>
                      )}
                    </div>
                  );
                })
              : Array.from({ length: metrics.cols }).map((_, i) => (
                  <div
                    key={i}
                    className="shrink-0 animate-pulse rounded-lg"
                    style={{
                      width: metrics.itemWidth,
                      height: metrics.itemHeight,
                      backgroundColor: 'hsl(var(--info))',
                    }}
                  />
                ))}
          </div>
        </div>
      ) : (
        /* CSS-only fallback before measurement */
        <div className="mt-1 flex gap-1 px-2 py-1">
          {Array.from({ length: 7 }).map((_, i) => (
            <div
              key={i}
              className="min-w-0 flex-1 animate-pulse rounded-lg"
              style={{
                aspectRatio: '2/3',
                backgroundColor: 'hsl(var(--info))',
              }}
            />
          ))}
        </div>
      )}

      {/* Footer */}
      <div
        className="flex shrink-0 items-center"
        style={{
          height: footerHeight ?? 40,
          padding: `0 ${footerPx ?? 12}px`,
          backgroundColor: 'rgba(255,255,255,0.5)',
        }}
      >
        {snapshot ? (
          <p className="truncate text-sm font-semibold text-black">
            {snapshot.name}
          </p>
        ) : (
          <div
            className="h-3 w-20 animate-pulse rounded"
            style={{ backgroundColor: 'hsl(var(--info))' }}
          />
        )}
      </div>
    </div>
  );
}
